import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {NzImageModule, NzImageService} from 'ng-zorro-antd/image';
import {NzIconModule} from 'ng-zorro-antd/icon';
import {TranslateModule} from '@ngx-translate/core';
import {HospitalityLink} from '../property-list/property-list.models';
import {HospitalityLinkViewModel, HospitalityLinkDocumentViewModel} from '../landing-page/landing-page.hospitality.mixin';

export interface HospitalityLinkDocument extends Partial<HospitalityLinkDocumentViewModel> {
  fileName?: string;
  fileType?: string;
  url?: string;
  previewUrl?: string;
  thumbnailUrl?: string;
}

export type HospitalityLinkPresenterItem = (HospitalityLink | HospitalityLinkViewModel) & {
  document?: HospitalityLinkDocument | null;
};

@Component({
  selector: 'app-hospitality-link-preview',
  standalone: true,
  imports: [CommonModule, NzImageModule, NzIconModule, TranslateModule],
  templateUrl: './hospitality-link-preview.component.html',
  styleUrls: ['./hospitality-link-preview.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HospitalityLinkPreviewComponent {
  private static readonly IMAGE_TYPES = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp']);
  private static readonly PDF_TYPES = new Set(['pdf']);

  private _links: HospitalityLinkPresenterItem[] = [];

  constructor(private readonly imageService: NzImageService) {}

  @Input()
  set links(value: HospitalityLinkPresenterItem[] | null | undefined) {
    this._links = Array.isArray(value) ? value : [];
  }

  get links(): HospitalityLinkPresenterItem[] {
    return this._links;
  }
  @Input() variant: 'grid' | 'list' = 'grid';
  @Input() showSourceBadge = false;
  @Input() propertySourceLabelKey = 'HOSPITALITY_FIELDS.PROPERTY_SPECIFIC_LABEL';
  @Input() companySourceLabelKey = 'HOSPITALITY_FIELDS.COMPANY_SELECTIONS_LABEL';

  @Output() linkClick = new EventEmitter<HospitalityLinkPresenterItem>();

  get hasLinks(): boolean {
    return Array.isArray(this._links) && this._links.length > 0;
  }

  get variantClass(): string {
    return this.variant === 'list' ? 'hospitality-link-presenter--list' : 'hospitality-link-presenter--grid';
  }

  trackByIndex(_: number, item: HospitalityLinkPresenterItem): string {
    return [item.label, item.url, item.document?.url].filter(Boolean).join('|');
  }

  onLinkClick(event: Event, link: HospitalityLinkPresenterItem): void {
    if (this.isImageDocument(link)) {
      event.preventDefault();
      event.stopPropagation();
      this.previewImage(link);
    }

    this.linkClick.emit(link);
  }

  resolveLabel(link: HospitalityLinkPresenterItem): string {
    return link.label || link.document?.fileName || link.url || link.document?.url || '';
  }

  getLinkUrl(link: HospitalityLinkPresenterItem): string | undefined {
    // 1. Prefer explicit document/url if present
    if (link.document?.url) {
      return link.document.url;
    }

    if (link.url) {
      return link.url;
    }

    // 2. Prefer human-readable address for Google Maps
    if (link.address) {
      const encodedAddress = encodeURIComponent(link.address);
      return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    }

    // 3. Fallback: use coordinates if available
    const coords: any = (link as any).coordinates;
    if (coords) {
      if (typeof coords === 'string') {
        // e.g. "48.8566,2.3522"
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(coords)}`;
      }

      if (typeof coords.lat === 'number' && typeof coords.lng === 'number') {
        return `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`;
      }
    }

    return undefined;
  }

  getImageSource(link: HospitalityLinkPresenterItem): string | undefined {
    return link.document?.thumbnailUrl || link.document?.previewUrl || link.document?.url;
  }

  getImagePreviewSource(link: HospitalityLinkPresenterItem): string | undefined {
    return link.document?.previewUrl || link.document?.url || link.url || undefined;
  }

  getDocumentExtension(link: HospitalityLinkPresenterItem): string {
    const doc = link.document;
    if (!doc) {
      return '';
    }
    const type = (doc.fileType || '').toLowerCase();
    if (type.includes('/')) {
      const parts = type.split('/');
      return parts[parts.length - 1];
    }
    if (type) {
      return type;
    }
    const fileName = doc.fileName || '';
    const match = /\.([a-zA-Z0-9]+)$/.exec(fileName);
    return match ? match[1].toLowerCase() : '';
  }

  isImageDocument(link: HospitalityLinkPresenterItem): boolean {
    const extension = this.getDocumentExtension(link);
    return extension ? HospitalityLinkPreviewComponent.IMAGE_TYPES.has(extension) : false;
  }

  isPdfDocument(link: HospitalityLinkPresenterItem): boolean {
    const extension = this.getDocumentExtension(link);
    return extension ? HospitalityLinkPreviewComponent.PDF_TYPES.has(extension) : false;
  }

  getIconType(link: HospitalityLinkPresenterItem): string {
    if (this.isPdfDocument(link)) {
      return 'file-pdf';
    }
    const extension = this.getDocumentExtension(link);
    if (extension === 'doc' || extension === 'docx') {
      return 'file-word';
    }
    if (extension === 'xls' || extension === 'xlsx') {
      return 'file-excel';
    }
    if (extension === 'ppt' || extension === 'pptx') {
      return 'file-ppt';
    }
    if (link.url) {
      if (/^mailto:/i.test(link.url)) {
        return 'mail';
      }
      if (/^tel:/i.test(link.url)) {
        return 'phone';
      }
    }
    if (link.coordinates) {
      return 'environment';
    }
    return 'link';
  }

  shouldOpenInNewTab(url?: string): boolean {
    if (!url) {
      return false;
    }
    return /^(https?:)?\/\//i.test(url);
  }

  private previewImage(link: HospitalityLinkPresenterItem): void {
    const src = this.getImagePreviewSource(link);
    if (!src) {
      return;
    }

    this.imageService.preview([
      {
        src,
        alt: this.resolveLabel(link),
      },
    ]);
  }
}
